import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProviderPriceListItemOption, {
  useProviderPriceListItemOptionForm,
} from '@hooks/useProviderPriceListItemOption'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditProviderPriceListItemOptionModal'

type ModalCallbacks = {
  closeModal: () => void,
  createProviderPriceListItemOption: (payload: HandleCallbackActionParams) => void,
  updateProviderPriceListItemOption: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditProviderPriceListItemOptionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createProviderPriceListItemOption, updateProviderPriceListItemOption } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const providerPriceListItemOption = digObject(modalPayload, 'providerPriceListItemOption', {})

  const newRecord = !providerPriceListItemOption.id

  const {
    callbacks: {
      createProviderPriceListItemOption: createFn,
      updateProviderPriceListItemOption: updateFn,
    },
    creating,
    updating,
  } = useProviderPriceListItemOption(providerPriceListItemOption)

  const {
    entityState,
    entityState: {
      product_code,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useProviderPriceListItemOptionForm(providerPriceListItemOption, { validateOn: 'change' })

  const createOrUpdateProviderPriceListItemOptionPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Price List Item Option ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Item Option"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Product Code" errorMessage={errors.product_code} marginTop="large">
            <input name="product_code" type="text" value={product_code} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createProviderPriceListItemOption(createOrUpdateProviderPriceListItemOptionPayload)
              : () => updateProviderPriceListItemOption(createOrUpdateProviderPriceListItemOptionPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Item Option` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderPriceListItemOptionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
